import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Users, CheckCircle2, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { programs } from "../mock";

const Programs = () => {
  const { toast } = useToast();
  const [active, setActive] = useState(null);

  const register = () => {
    toast({ title: "Interest received! 🎉", description: `We'll reach out about ${active?.title} soon.` });
    setActive(null);
  };

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative text-center max-w-3xl mx-auto">
          <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Programs & Classes</Badge>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">Joyful learning for every age & stage</h1>
          <p className="mt-5 text-lg text-muted-foreground">Explore our enrichment classes and support programs. All offered on a sliding scale, with scholarships available.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container-prc grid md:grid-cols-2 gap-8">
          {programs.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.id} delay={(i % 2) * 90}>
                <div className="card-hover bg-white rounded-2xl overflow-hidden shadow-soft h-full flex flex-col md:flex-row">
                  <div className="img-zoom md:w-2/5 h-52 md:h-auto">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6 md:w-3/5 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`grid place-items-center h-10 w-10 rounded-xl ${p.color}`}><Icon className="h-5 w-5" /></span>
                      <Badge variant="outline" className="rounded-full font-semibold">{p.age}</Badge>
                    </div>
                    <h3 className="font-display font-bold text-xl text-foreground">{p.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{p.description}</p>
                    <Button onClick={() => setActive(p)} className="btn-cta mt-5 self-start bg-primary hover:bg-primary/90 rounded-full font-bold px-6">
                      Register Interest <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-16 bg-secondary/40">
        <div className="container-prc grid md:grid-cols-3 gap-6">
          {[
            { icon: Clock, title: "Flexible schedules", text: "Morning, afternoon, and weekend sessions to fit your family." },
            { icon: Users, title: "Small group sizes", text: "Personal attention from caring, experienced educators." },
            { icon: CheckCircle2, title: "Sliding-scale pricing", text: "Pay what you can—scholarships available for every program." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.title} delay={i * 80} className="bg-white rounded-2xl p-7 shadow-soft">
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-accent/10 text-accent mb-4"><Icon className="h-6 w-6" /></span>
                <h3 className="font-display font-bold text-lg text-foreground">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.text}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-20">
        <div className="container-prc text-center">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl text-foreground">Not sure where to start?</h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Our family educators are happy to help you find the perfect fit.</p>
            <Button asChild className="btn-cta mt-7 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full px-7 h-12"><Link to="/contact">Talk to Us <ArrowRight className="h-5 w-5 ml-2" /></Link></Button>
          </Reveal>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Register interest — {active?.title}</DialogTitle>
            <DialogDescription>{active?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <input className="w-full h-11 rounded-xl border border-input px-4 text-sm" placeholder="Your name" />
            <input className="w-full h-11 rounded-xl border border-input px-4 text-sm" placeholder="Email address" />
            <input className="w-full h-11 rounded-xl border border-input px-4 text-sm" placeholder="Child's age (optional)" />
            <Button onClick={register} className="btn-cta w-full bg-primary hover:bg-primary/90 rounded-full font-bold h-11"><Heart className="h-4 w-4 mr-2" /> Submit Interest</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Programs;
